import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Volleyball, CreditCard, CheckCircle, ArrowLeft, Phone, UploadSimple, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZELLE_PHONE = "5618596830";

export default function CourtRentalPublic() {
  const { t } = useTranslation();
  const [info, setInfo] = useState({ price_public: 50, price_student: 40 });
  const [form, setForm] = useState({ name: '', email: '', phone: '', date: '', start_time: '', end_time: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [rentalId, setRentalId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/court-rental/info`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone || !form.date || !form.start_time || !form.end_time) {
      return toast.error(t('courtRental.missingFields'));
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/court-rental/register`, form);
      setRentalId(data.id);
      setStep('choose-payment');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('courtRental.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const payCard = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/court-rental/checkout`, { rental_id: rentalId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('courtRental.checkoutError'));
      setLoading(false);
    }
  };

  const uploadZelle = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/public/court-rental/zelle-receipt?rental_id=${rentalId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('common.receiptSent'));
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setUploading(false);
    }
  };

  const totalCard = (info.price_public * 1.05).toFixed(2);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center text-[#73706A] hover:text-[#E85D04] mb-4 text-sm">
          <ArrowLeft size={16} className="mr-1" /> {t('common.back')}
        </Link>
        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden" data-testid="court-rental-public-page">
          <div className="h-28 bg-gradient-to-br from-[#0077B6] to-[#03045E] relative">
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute bottom-0 left-0 p-5 text-white">
              <p className="text-xs uppercase tracking-wider opacity-90">Tia Karol Sports</p>
              <h1 className="font-['Outfit'] text-2xl font-bold">{t('courtRental.title')}</h1>
              <p className="text-sm opacity-90">{t('courtRental.subtitleHeader', { publicPrice: info.price_public, studentPrice: info.price_student })}</p>
            </div>
          </div>

          <div className="p-5 sm:p-6 space-y-4">
            {step === 'form' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label htmlFor="cr-name" className="text-xs">{t('courtRental.fields.fullName')} *</Label>
                    <Input id="cr-name" value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="court-rental-name" />
                  </div>
                  <div>
                    <Label htmlFor="cr-email" className="text-xs">{t('courtRental.fields.email')}</Label>
                    <Input id="cr-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="court-rental-email" />
                  </div>
                  <div>
                    <Label htmlFor="cr-phone" className="text-xs">{t('courtRental.fields.phone')} *</Label>
                    <Input id="cr-phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(305) 123-4567" data-testid="court-rental-phone" />
                  </div>
                  <div>
                    <Label htmlFor="cr-date" className="text-xs">{t('courtRental.fields.date')} *</Label>
                    <Input id="cr-date" type="date" value={form.date} onChange={(e) => update('date', e.target.value)} data-testid="court-rental-date" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="cr-start" className="text-xs">{t('courtRental.fields.start')} *</Label>
                      <Input id="cr-start" type="time" value={form.start_time} onChange={(e) => update('start_time', e.target.value)} data-testid="court-rental-start" />
                    </div>
                    <div>
                      <Label htmlFor="cr-end" className="text-xs">{t('courtRental.fields.end')} *</Label>
                      <Input id="cr-end" type="time" value={form.end_time} onChange={(e) => update('end_time', e.target.value)} data-testid="court-rental-end" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="cr-notes" className="text-xs">{t('courtRental.fields.notes')}</Label>
                    <Textarea id="cr-notes" rows={2} value={form.notes} onChange={(e) => update('notes', e.target.value)} data-testid="court-rental-notes" />
                  </div>
                </div>
                <div className="bg-[#FFF3E6] border-l-4 border-[#E85D04] rounded-r-lg p-3">
                  <p className="text-sm text-[#1A1A1A]">
                    <strong>{t('courtRental.studentBanner1')}</strong> {t('courtRental.studentBanner2')} <Link to="/login" className="underline text-[#E85D04] font-semibold">{t('courtRental.portalLink')}</Link> {t('courtRental.studentBanner3')} <strong>${info.price_student}</strong>.
                  </p>
                </div>
                <Button onClick={submit} disabled={loading} className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3" data-testid="court-rental-submit-btn">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Volleyball size={18} className="mr-2" />}
                  {t('courtRental.reserveAndPay')}
                </Button>
              </>
            )}

            {step === 'choose-payment' && (
              <div className="space-y-4" data-testid="court-rental-choose-payment">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                  <CheckCircle size={20} className="text-[#2D6A4F] mt-0.5" weight="fill" />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{t('courtRental.reservedTitle')}</p>
                    <p className="text-xs text-[#73706A]"><Clock size={12} className="inline" /> {form.date} {form.start_time} - {form.end_time}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button onClick={payCard} disabled={loading} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white py-4 flex-col h-auto" data-testid="court-rental-pay-card-btn">
                    <div className="flex items-center"><CreditCard size={20} className="mr-2" /> {t('common.card')}</div>
                    <span className="text-xs opacity-90 mt-1">${totalCard} {t('courtRental.cardIncludesFee')}</span>
                  </Button>
                  <Button onClick={() => setStep('zelle')} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4 flex-col h-auto" data-testid="court-rental-zelle-btn">
                    <div className="flex items-center"><Phone size={20} className="mr-2" /> Zelle</div>
                    <span className="text-xs mt-1">${info.price_public}</span>
                  </Button>
                </div>
              </div>
            )}

            {step === 'zelle' && (
              <div className="space-y-4 text-center" data-testid="court-rental-zelle-step">
                <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto"><Phone size={28} className="text-white" /></div>
                <p className="text-[#73706A]">{t('courtRental.zelleHeader')} <strong className="text-[#E85D04]">${info.price_public}</strong> {t('courtRental.zelleHeaderSuffix')}</p>
                <div className="bg-[#F5F2EB] rounded-lg p-4">
                  <p className="text-2xl font-bold text-[#1A1A1A] tracking-wider">{ZELLE_PHONE}</p>
                  <p className="text-xs text-[#73706A] mt-1">Tia Karol Sports</p>
                </div>
                <label className="cursor-pointer block">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadZelle(e.target.files[0])} data-testid="court-rental-zelle-upload" />
                  <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploading}>
                    {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={18} className="mr-2" />}
                    {uploading ? t('common.sending') : t('common.uploadReceiptShort')}
                  </Button>
                </label>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center py-6 space-y-3" data-testid="court-rental-success">
                <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto"><CheckCircle size={32} className="text-white" weight="fill" /></div>
                <h2 className="font-['Outfit'] text-xl font-bold">{t('courtRental.confirmedTitle')}</h2>
                <p className="text-sm text-[#73706A]">{form.date} {form.start_time} - {form.end_time}</p>
                <Link to="/"><Button variant="outline" className="mt-3">{t('courtRental.backHome')}</Button></Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
